import { Badge, Box, ColumnLayout, Container, SpaceBetween } from '@amzn/awsui-components-react';
import type { AccountCoverageTotals, KpiBucket } from '@amzn/ivthub-client';
import { ABSENT_MARK, formatArr } from './scale';

/**
 * The card above 我的灯塔客户 / 圈外归仓.
 *
 * ⭐ `coverage` is over EVERY ACCOUNT IN SCOPE — not this page, and not the sum of the page's
 * rows. So it is read straight from the response and nothing here adds rows up. This is the one
 * place on the account blocks where a figure genuinely does not reconcile with what is on
 * screen, and the note under the card says so rather than leaving the reader to discover it.
 *
 * ⭐ `Coverage Pipeline` is `tagged` + `untagged`, in BOTH the amount and the count. The server
 * has no member for it and does not need one: an opportunity is on exactly one side of the tag,
 * so the addition cannot double-count. It is computed here for that reason, not as a fallback.
 */

const bucketOf = (b: KpiBucket | undefined) => ({ oppCount: b?.oppCount ?? 0, arr: b?.arr ?? 0 });

/**
 * Narrowest a metric column may get before the row reflows (3 → 2 → 1). Set by the widest value
 * the cell has to hold on one line — an eight-character amount like `$123.45M` at the value's
 * 28px is ~125px — plus room to spare. ColumnLayout's own breakpoints are the wrong trigger here:
 * without this it keeps three columns only above a 688px CONTAINER, so a half-width card put the
 * third metric on a row of its own even with most of the row empty.
 */
const MIN_METRIC_PX = 150;

/** The card title. Figma: 14px/700, not the 20px of a Cloudscape h2 `Header`. */
function CardTitle({ children }: { children: string }) {
  return (
    <Box variant="h2" fontSize="heading-xs" padding="n">
      {children}
    </Box>
  );
}

/**
 * One metric, typed to the comp (4146:52491, measured off the 1:1 render): label 12px regular and
 * value 28px regular — larger than any Cloudscape Box size, hence the class.
 *
 * DAT-9259 item 6: the COUNT is on top in the large (28px) style and the ARR sits beneath it
 * in the small body-s secondary style. Count is more important than ARR, and the STO View's
 * other count-stacked UI (KpiBand, IndustryTable, AssetTable) all put count-on-top.
 *
 * The count carries the "opps" unit at 12px/400 on the same baseline, mirroring the KpiBand's
 * value + unit shape so the page uses one noun for the count across every surface. Pass
 * `count` as a NUMBER so the unit is only rendered when there is a real count — a `string`
 * caller would have to concatenate "opps" at every site and the placeholder (dash) would
 * either carry a wrong unit or a stripped one.
 *
 * `arr` is optional: before a response arrives the card prints a bare dash on the large (count)
 * line and nothing below it — printing an ARR dash too would double a signal that is already
 * clear from one.
 */
function MetricCell({ label, count, arr }: { label: string; count: number | undefined; arr?: string }) {
  return (
    <div>
      <div className="ivt-dashboard__coverage-label">{label}</div>
      <div className="ivt-dashboard__coverage-value">
        {count === undefined ? (
          ABSENT_MARK
        ) : (
          <>
            {count} <span className="ivt-dashboard__coverage-unit">opps</span>
          </>
        )}
      </div>
      {arr !== undefined && (
        <Box fontSize="body-s" color="text-body-secondary">
          {arr}
        </Box>
      )}
    </div>
  );
}

export interface CoverageCardProps {
  coverage: AccountCoverageTotals | undefined;
  /**
   * Picks the block, exactly as it does on the request: `true` = 我的灯塔客户, `false` =
   * 圈外归仓. Not a title string, so the card cannot be labelled as one block while being fed
   * the other's figures.
   */
  lighthouse: boolean;
}

export function CoverageCard({ coverage, lighthouse }: CoverageCardProps) {
  const title = lighthouse ? '我的灯塔客户' : '圈外归仓（非灯塔客户）';

  // No response yet. Zeroes are NOT used as the placeholder: `accountCount` of 0 is a real and
  // meaningful answer for the reclaim block, so printing it before the answer arrives would be
  // indistinguishable from the answer itself. The dash sits on the large (count) line because
  // that is the one line this placeholder renders.
  if (coverage === undefined) {
    return (
      <Container header={<CardTitle>{title}</CardTitle>}>
        <ColumnLayout columns={3} minColumnWidth={MIN_METRIC_PX}>
          {['Coverage Pipeline', 'Tagged', 'Untagged'].map((label) => (
            <MetricCell key={label} label={label} count={undefined} />
          ))}
        </ColumnLayout>
      </Container>
    );
  }

  const tagged = bucketOf(coverage.tagged);
  const untagged = bucketOf(coverage.untagged);
  const pipeline = {
    oppCount: tagged.oppCount + untagged.oppCount,
    arr: tagged.arr + untagged.arr,
  };
  const accountCount = coverage.accountCount ?? 0;

  const cells: { label: string; bucket: { oppCount: number; arr: number } }[] = [
    { label: 'Coverage Pipeline', bucket: pipeline },
    { label: 'Tagged', bucket: tagged },
    { label: 'Untagged', bucket: untagged },
  ];

  return (
    <Container
      // No counter: the account block below carries the same triple.
      header={<CardTitle>{title}</CardTitle>}
    >
      <SpaceBetween size="l">
        {/* Derived entirely from the untagged bucket — untagged IS the work. Suppressed at zero
            rather than printed as "0 笔待归仓": a prompt for nothing to do is noise, and the
            Untagged cell below already states the zero. */}
        {untagged.oppCount > 0 && (
          <Box>
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Badge color="red">要做的事</Badge>
              <Box fontWeight="bold">
                查看未打标的 {untagged.oppCount} 笔 {formatArr(untagged.arr)} 是否需要归仓
              </Box>
            </SpaceBetween>
          </Box>
        )}

        {/* Default variant, not `text-grid`: the comp draws no dividers between the metrics, and
            `text-grid` does in both of ColumnLayout's modes. */}
        <ColumnLayout columns={3} minColumnWidth={MIN_METRIC_PX}>
          {/* One order for both cards, though the comp swaps them, so the two can be read
              against each other. */}
          {cells.map(({ label, bucket }) => (
            <MetricCell
              key={label}
              label={label}
              count={bucket.oppCount}
              arr={formatArr(bucket.arr)}
            />
          ))}
        </ColumnLayout>

        {/* Says the one thing about these numbers that cannot be inferred from them. Without it
            a reader who adds the list's account headers up and gets a smaller number has no way
            to tell a scope difference from a bug. */}
        <Box fontSize="body-s" color="text-body-secondary">
          以上为范围内全部 {accountCount} 个客户的合计，不是当前页的小计。
        </Box>
      </SpaceBetween>
    </Container>
  );
}
